import { useState } from 'react';
import { IconChevronDown, IconFolder } from '@tabler/icons-react';
import { isEmpty } from 'lodash-es';
import { useFela } from 'react-fela';
import { FormattedMessage } from 'react-intl';

import { VaultRole } from '@/modules/api/codegen';
import { useIsAdmin } from '@/modules/auth/hooks';
import { MenuItem } from '@/modules/layout';

import { useVaults } from '../../hooks';
import { ManageVaultsLink } from '../ManageVaultsLink';
import { VaultLink } from '../VaultLink';
import * as felaRules from './VaultsSection.rules';

export const VaultsSection = () => {
    const [expanded, setExpanded] = useState(true);
    const { css } = useFela({ expanded });

    const { personalVault, teamVaults } = useVaults();
    const isAdmin = useIsAdmin();

    const vaults = personalVault ? [personalVault, ...teamVaults] : teamVaults;

    // Editing and deleting a team vault are vault-role rights, but the Manage Vaults page lives under
    // the app settings, which only the admin menu entry leads to. This is the editor's way in.
    const canManageVaults = !isAdmin && teamVaults.some(({ userRole }) => userRole === VaultRole.editor);

    if (isEmpty(vaults)) {
        return null;
    }

    return (
        <div className={css(felaRules.container)}>
            <MenuItem
                icon={IconFolder}
                trailing={<IconChevronDown size={16} className={css(felaRules.chevron)} />}
                button={{
                    type: 'button',
                    'aria-expanded': expanded,
                    onClick: () => setExpanded(isExpanded => !isExpanded),
                }}
            >
                <FormattedMessage id='menu.vaults' />
            </MenuItem>
            {/* Kept mounted so the collapse can animate; `aria-hidden` is what takes it out of the
                accessibility tree, since the row still occupies the DOM while it plays. */}
            <div className={css(felaRules.collapsible, expanded && felaRules.collapsibleExpanded)} aria-hidden={!expanded}>
                <div className={css(felaRules.collapsibleInner)}>
                    <div className={css(felaRules.vaults)}>
                        {vaults.map(vault => (
                            <VaultLink key={vault.id} vault={vault} />
                        ))}
                        {canManageVaults ? <ManageVaultsLink /> : null}
                    </div>
                </div>
            </div>
        </div>
    );
};
